import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { IUBENDA_PURPOSE, onIubendaConsent } from '../utils/iubenda-consent';

/**
 * Whether the user granted the Iubenda "Measurement" purpose (purpose 4).
 *
 * Two sources, both needed:
 * - PostHog's persisted consent status covers returning consenters on pages
 *   where Iubenda is not loaded at all (e.g. /getting-started, where the banner
 *   is intentionally absent). PostHog may finish init after this hook mounts
 *   (child effects run before the provider's), so we re-check on the
 *   `onFeatureFlags` signal — flags only load once init has completed.
 * - The `iubenda:consent` bridge event covers live grants and revocations
 *   while the page is open (Iubenda re-emits the stored preference on load).
 */
export function useMeasurementConsent(): boolean {
  const [granted, setGranted] = useState(false);

  useEffect(() => {
    const syncFromPostHog = () => {
      if (posthog.__loaded && posthog.get_explicit_consent_status() === 'granted') setGranted(true);
    };
    syncFromPostHog();
    const unsubscribeFlags = posthog.onFeatureFlags(syncFromPostHog);
    const unsubscribeIubenda = onIubendaConsent(IUBENDA_PURPOSE.MEASUREMENT, setGranted);
    return () => {
      unsubscribeFlags();
      unsubscribeIubenda();
    };
  }, []);

  return granted;
}
